<template>
  <div id="order-queries-history" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0" :ref="pageRef">{{ $route.meta.title }}</h2>

        <v-row>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === APPROVED }"
              @click="handleViewChange(APPROVED)"
            >
              Approved
            </v-btn>
          </v-col>
          <v-col cols="12" sm="4" md="3" lg="2">
            <v-btn
              large
              class="i-btn-select g-full-width"
              :class="{ 'i-btn-select-active': selectedView === REJECTED }"
              @click="handleViewChange(REJECTED)"
            >
              Rejected
            </v-btn>
          </v-col>

          <v-spacer></v-spacer>

          <v-col cols="12" sm="4" md="3" lg="2">
            <i-instructions
              instructionKey="reports/order-queries-history"
              :instructionSection="instructionSection"
            ></i-instructions>
          </v-col>
        </v-row>

        <i-search-sort
          ref="searchSort"
          :loading="loading"
          dateRangeColumnName="Created Date"
          :routeName="$route.name"
          @searchQuery="searchQuery"
        />

        <i-progress-linear v-if="loading && $vuetifyDisplay.smAndDown" />

        <template v-if="!loading && orderQueries.length === 0">
          <i-empty-results
            buttonText="Back to Reports"
            buttonRouteName="reports"
            :title="emptyFilteredResultsText"
          />
        </template>

        <template v-else>
          <template v-if="$vuetifyDisplay.smAndDown">
            <v-row no-gutters>
              <v-col
                cols="12"
                v-for="item in orderQueries"
                :key="item.id"
                class="mt-0 pa-0"
              >
                <v-card class="rounded-2 pa-4 pa-md-6 mb-6">
                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span
                        ><strong>{{ headers[0].text }}</strong></span
                      >
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span
                        ><strong>{{ item.referenceNumber }}</strong></span
                      >
                    </v-col>
                  </v-row>

                  <v-divider class="my-2"></v-divider>

                  <v-row no-gutters>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[1].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ item.retailerOrderReferences[0] }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[2].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ item.supplierOrderNumber }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[3].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.orderDateTime)
                      }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[4].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.createdAt)
                      }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[5].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.formatDateTime12h(item.resolvedAt)
                      }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[6].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{ item.storeName }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1">
                      <span>{{ headers[7].text }}</span>
                    </v-col>
                    <v-col cols="6" class="py-1 pl-4">
                      <span>{{
                        $filters.currency(
                          displayCreditAmount(item.requestedCreditAmount)
                        )
                      }}</span>
                    </v-col>
                  </v-row>

                  <v-divider class="my-2"></v-divider>

                  <p
                    v-if="item.status === 'REJECTED'"
                    class="g-bold g-border-radius-s i-status-block-red px-2 py-3 text-center"
                  >
                    <strong>QUERY STATUS: REJECTED</strong>
                  </p>
                  <p
                    v-if="item.status === 'APPROVED'"
                    class="g-bold g-border-radius-s i-status-block-green px-2 py-3 text-center"
                  >
                    <strong
                      >QUERY STATUS:
                      {{ item.hasRejectedIssues ? 'PARTIALLY' : '' }}
                      APPROVED</strong
                    >
                  </p>

                  <v-divider class="my-2"></v-divider>

                  <v-btn
                    rounded
                    class="i-btn-block-secondary g-full-width"
                    :to="{
                      name: 'order-query-details',
                      params: { id: item.id }
                    }"
                  >
                    <strong> View </strong>
                  </v-btn>
                  <div
                    class="text-center pt-2"
                    v-if="
                      item.status === 'APPROVED' &&
                      item.paymentTransactionExpectedPeriodEndDate
                    "
                  >
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-1"
                      :to="paymentTransactionsRouteTo(item)"
                    >
                      <span>Payment Transaction Details</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-list-box-outline</v-icon
                      >
                    </v-btn>
                  </div>
                  <div
                    class="text-center pt-2"
                    v-if="item.status === 'APPROVED'"
                  >
                    <v-btn
                      class="g-full-width i-btn-block-primary mt-1"
                      @click="fetchPDF(item.id)"
                    >
                      <span>Export PDF</span>
                      <v-icon color="white" class="ml-1"
                        >mdi-file-pdf-box</v-icon
                      >
                    </v-btn>
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </template>

          <template v-else>
            <v-row no-gutters>
              <v-col cols="12">
                <v-data-table
                  class="mt-2 mt-md-4 pt-2 elevation-1 i-data-table"
                  :headers="headersForSelectedView"
                  :loading="loading"
                  :items="orderQueries"
                  :items-per-page="pageSize"
                  :no-data-text="emptyFilteredResultsText"
                  :no-results-text="emptyFilteredResultsText"
                  hide-default-footer
                >
                  <template #[`header.actions`]="{ header }">
                    {{ header.text }}
                    <v-icon
                      class="i-tooltip-color pb-1"
                      small
                      v-tooltip="
                        'PDF only available for APPROVED credit claims'
                      "
                    >
                      mdi-help-circle-outline
                    </v-icon>
                  </template>

                  <template v-slot:[`header.referenceNumber`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('referenceNumber')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('referenceNumber')" small>
                        {{
                          isSortAsc('referenceNumber')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.supplierOrderNumber`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('supplierOrderNumber')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('supplierOrderNumber')" small>
                        {{
                          isSortAsc('supplierOrderNumber')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.orderDateTime`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('orderDateTime')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('orderDateTime')" small>
                        {{
                          isSortAsc('orderDateTime')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.createdAt`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('createdAt')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('createdAt')" small>
                        {{
                          isSortAsc('createdAt')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.resolvedAt`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('resolvedAt')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('resolvedAt')" small>
                        {{
                          isSortAsc('resolvedAt')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`header.storeName`]="{ header }">
                    <span
                      class="i-sortable-header"
                      @click="handleHeaderSort('storeName')"
                    >
                      {{ header.text }}
                      <v-icon v-if="isSortedBy('storeName')" small>
                        {{
                          isSortAsc('storeName')
                            ? 'mdi-arrow-up'
                            : 'mdi-arrow-down'
                        }}
                      </v-icon>
                    </span>
                  </template>

                  <template v-slot:[`item.referenceNumber`]="{ item }">
                    <span>{{ item.referenceNumber }}</span>
                  </template>

                  <template v-slot:[`item.retailerOrderReference`]="{ item }">
                    <span>{{ item.retailerOrderReferences[0] }}</span>
                  </template>

                  <template v-slot:[`item.storeName`]="{ item }">
                    <span>{{ item.storeName }}</span>
                  </template>

                  <template v-slot:[`item.supplierOrderNumber`]="{ item }">
                    <span>{{ item.supplierOrderNumber }}</span>
                  </template>

                  <template v-slot:[`item.orderDateTime`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.orderDateTime)
                    }}</span>
                  </template>

                  <template v-slot:[`item.createdAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.createdAt)
                    }}</span>
                  </template>

                  <template v-slot:[`item.resolvedAt`]="{ item }">
                    <span>{{
                      $filters.formatDateTime12h(item.resolvedAt)
                    }}</span>
                  </template>

                  <template v-slot:[`item.requestedCreditAmount`]="{ item }">
                    <span>{{
                      $filters.currency(
                        displayCreditAmount(item.requestedCreditAmount)
                      )
                    }}</span>
                  </template>

                  <template v-slot:[`item.orderQuery`]="{ item }">
                    <v-btn
                      rounded
                      class="i-btn-block-secondary p-btn"
                      :to="{
                        name: 'order-query-details',
                        params: { id: item.id }
                      }"
                    >
                      <span><strong>View</strong></span>
                    </v-btn>
                  </template>

                  <template v-slot:[`item.actions`]="{ item }">
                    <v-btn
                      :disabled="!item.paymentTransactionExpectedPeriodEndDate"
                      icon
                      :to="paymentTransactionsRouteTo(item)"
                      v-tooltip="{
                        content: 'View related Payment Transaction details',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                    >
                      <v-icon class="i-btn-payment-transactions"
                        >mdi-list-box-outline</v-icon
                      >
                    </v-btn>
                    <v-btn
                      :disabled="item.status !== 'APPROVED'"
                      icon
                      v-tooltip="{
                        content: 'Export Order Query PDF',
                        visible: $vuetifyDisplay.mdAndDown ? false : undefined
                      }"
                      @click="fetchPDF(item.id)"
                    >
                      <v-icon class="i-btn-pdf">mdi-file-pdf-box</v-icon>
                    </v-btn>
                  </template>

                  <template v-slot:[`item.status`]="{ item }">
                    <p
                      v-if="item.status === 'REJECTED'"
                      class="g-bold g-border-radius-s i-status-block-red px-2 py-2 my-1 text-center"
                      v-resize-text="{
                        minFontSize: '13px',
                        maxFontSize: '15px'
                      }"
                    >
                      REJECTED
                    </p>
                    <p
                      v-if="item.status === 'APPROVED'"
                      class="g-bold g-border-radius-s i-status-block-green px-2 py-2 my-1 text-center"
                      v-resize-text="{
                        minFontSize: '13px',
                        maxFontSize: '15px'
                      }"
                    >
                      {{ item.hasRejectedIssues ? 'PARTIALLY' : '' }} APPROVED
                    </p>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </template>

          <i-paginator
            :currentPage="currentPage"
            :pageSize="pageSize"
            :pagination="pagination"
            @paginate="handlePagination"
          />
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import {
  fetchOrderQueries,
  fetchOrderQueryPdfUrl
} from '@/services/orderQueries'
import iPaginator from '@/components/Paginator'
import iProgressLinear from '@/components/ProgressLinear'
import iSearchSort from '@/components/SearchSort'
import { openFile, routeToPaymentTransactions } from '@/utils/common'
import iInstructions from '@/components/Instructions'
import iEmptyResults from '@/components/EmptyResults'
import { pickSupplierCostTotal } from '@/utils/supplierAmountDisplay'

const ORDER_QUERIES_STATUS = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  OUTSTANDING: 'OUTSTANDING'
}

export default {
  components: {
    iEmptyResults,
    iInstructions,
    iPaginator,
    iProgressLinear,
    iSearchSort
  },

  data: () => ({
    loading: false,
    currentPage: 1,
    pageSize: 10,
    pageRef: 'pageTitle',
    pagination: {
      total: 0,
      totalPages: 0
    },
    query: null,
    orderQueries: [],
    selectedView: ORDER_QUERIES_STATUS.APPROVED,
    ...ORDER_QUERIES_STATUS
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapGetters('orderQueries', [
      'approvedOrderQueries',
      'rejectedOrderQueries'
    ]),
    ...mapGetters('tenant', [
      'isILR',
      'tenantCountryRegion',
      'usesAustralianTaxLabels'
    ]),

    headers() {
      return [
        {
          text: 'ORDER QUERY REFERENCE NUMBER',
          value: 'referenceNumber',
          sortable: false,
          width: '8%'
        },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'} ORDER NUMBER`,
          value: 'retailerOrderReference',
          sortable: false,
          width: '8%'
        },
        {
          text: 'PO NUMBER',
          value: 'supplierOrderNumber',
          sortable: false,
          width: '7%'
        },
        {
          text: 'ORDER DATE & TIME',
          value: 'orderDateTime',
          sortable: false,
          width: '11%'
        },
        {
          text: 'CREATED DATE',
          value: 'createdAt',
          sortable: false,
          width: '11%'
        },
        {
          text: 'RESOLVED DATE',
          value: 'resolvedAt',
          sortable: false,
          width: '11%'
        },
        {
          text: `${this.isILR ? 'CUSTOMER' : 'RETAILER'}`,
          value: 'storeName',
          sortable: false
        },
        {
          text: this.usesAustralianTaxLabels
            ? 'TOTAL CREDIT AMOUNT (INC TAX)'
            : 'TOTAL CREDIT AMOUNT',
          value: 'requestedCreditAmount',
          align: 'center',
          sortable: false,
          width: '8%'
        },
        {
          text: 'DETAILS',
          value: 'orderQuery',
          align: 'center',
          sortable: false,
          width: 80
        },
        {
          text: 'ACTIONS',
          value: 'actions',
          align: 'center',
          sortable: false,
          width: 100,
          hideForRejected: true
        },
        {
          text: 'STATUS',
          value: 'status',
          align: 'center',
          sortable: false,
          width: 140
        }
      ]
    },

    instructionSection() {
      let view = null
      switch (this.selectedView) {
        case ORDER_QUERIES_STATUS.APPROVED:
          view = 'Approved'
          break
        case ORDER_QUERIES_STATUS.REJECTED:
          view = 'Rejected'
          break
      }
      return view
    },

    emptyFilteredResultsText() {
      return `No ${this.selectedView} Order Queries found`
    },

    headersForSelectedView() {
      return this.selectedView === ORDER_QUERIES_STATUS.REJECTED
        ? this.headers.filter((h) => !h.hideForRejected)
        : this.headers
    }
  },

  methods: {
    ...mapActions('notifications', ['addNotification']),

    handleHeaderSort(value) {
      this.$refs.searchSort?.sortElements(value)
    },

    isSortedBy(value) {
      return this.$refs.searchSort?.sortBy === value
    },

    isSortAsc(value) {
      return !!this.$refs.searchSort?.sortByOptions.find(
        (option) => option.value === value
      )?.asc
    },

    displayCreditAmount(amount) {
      return pickSupplierCostTotal({
        tenantCountryRegion: this.tenantCountryRegion,
        cost: amount
      })
    },

    fetch(supplierId, currentPage, pageSize, status, query) {
      this.loading = true
      currentPage -= 1 // currentPage decreased by 1 as currentPage API starts from 0

      return fetchOrderQueries(supplierId, currentPage, pageSize, status, query)
        .then((res) => {
          this.orderQueries = res.queries
          this.pagination = res.pagination
        })
        .catch(() => {
          this.addNotification({ color: 'error', defaultError: true })
        })
        .finally(() => {
          this.loading = false
        })
    },

    fetchPDF(orderId) {
      fetchOrderQueryPdfUrl(orderId)
        .then((response) => {
          openFile(response.url)
        })
        .catch((err) => {
          err.response.status === 404
            ? this.addNotification({
                color: 'error',
                text: `Invoice ${orderId} not found`
              })
            : this.addNotification({ color: 'error', defaultError: true })
        })
    },

    handlePagination(page) {
      this.currentPage = page
      this.loading = true
      this.orderQueries = []
      this.fetch(
        this.selectedSupplierId,
        this.currentPage,
        this.pageSize,
        this.selectedView,
        this.query
      )
      if (this.$refs[this.pageRef])
        this.$refs[this.pageRef].scrollIntoView({ behavior: 'smooth' })
    },

    handleViewChange(status) {
      this.selectedView = status
      this.currentPage = 1
      this.loading = true
      this.orderQueries = []
      this.fetch(
        this.selectedSupplierId,
        this.currentPage,
        this.pageSize,
        this.selectedView,
        this.query
      )
    },

    searchQuery(queryObject) {
      if (queryObject.restartPagination) this.currentPage = 1

      this.query = queryObject.query
      this.fetch(
        this.selectedSupplierId,
        this.currentPage,
        this.pageSize,
        this.selectedView,
        this.query
      )
    },

    paymentTransactionsRouteTo(orderQuery) {
      return routeToPaymentTransactions(
        orderQuery,
        orderQuery.supplierOrderNumber
      )
    }
  },

  mounted() {
    this.fetch(
      this.selectedSupplierId,
      this.currentPage,
      this.pageSize,
      this.selectedView
    )
  },

  watch: {
    approvedOrderQueries() {
      if (this.selectedView === this.APPROVED) {
        this.fetch(this.currentPage, this.pageSize, this.selectedView)
      }
    },

    rejectedOrderQueries() {
      if (this.selectedView === this.REJECTED) {
        this.fetch(this.currentPage, this.pageSize, this.selectedView)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#order-queries-history {
  .p-btn-pdf {
    color: $link-color;
    &:before {
      font-size: 32px;
    }
  }
}

@media only screen and (min-width: 1264px) {
  #order-queries-history {
    .p-btn {
      width: 125px;
    }
  }
}
</style>
